/**
 * Tabela no formato do contrato da API (plano §5.1), renderizada com AG Grid Community.
 *
 * O backend decide o estilo de cada célula (`estilos["<linha>:<campo>"]`), como o
 * `style_table` do legado; aqui só se mapeia a classe para a cor.
 */
import { Button, Group, Stack } from '@mantine/core';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  type CellStyle,
  type ColDef,
  type ValueFormatterParams,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useMemo, useRef } from 'react';

import { formatarData, formatarDataHora } from '../lib/formatos';

ModuleRegistry.registerModules([AllCommunityModule]);

export type TipoColuna = 'num' | 'pct' | 'data' | 'datahora' | 'link' | 'texto';

export interface ColunaApi {
  campo: string;
  titulo: string;
  tipo: TipoColuna;
  casas?: number;
  /** Ocupa o espaço livre da tabela (ex.: nomes longos). */
  expandir?: boolean;
  /** Tooltip do título da coluna. */
  descricao?: string;
}

export interface TabelaApi {
  colunas: ColunaApi[];
  linhas: Record<string, unknown>[];
  estilos?: Record<string, string>;
  /** Tooltip por célula (`"<linha>:<campo>"`, como `estilos`); célula vazia com nota vira "—". */
  notas?: Record<string, string>;
}

const CORES: Record<string, string> = {
  negativo: '#c92a2a',
  positivo: '#2b8a3e',
  destaque: '#0d2340',
  'linha-destaque': '#add8e6', // lightblue, como highlight_row_if_value_equals do legado
  // Quartis (1 = mais baixo), paleta do highlight_quartile do legado (amarelo → verde).
  'quartil-1': '#faf099',
  'quartil-2': '#cbe08c',
  'quartil-3': '#96ce7e',
  'quartil-4': '#66ba7b',
};

const numero =
  (casas: number, sufixo = '') =>
  (p: ValueFormatterParams) =>
    typeof p.value === 'number'
      ? `${p.value.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })}${sufixo}`
      : '';

export function formatador(coluna: ColunaApi) {
  const casas = coluna.casas ?? 2;
  if (coluna.tipo === 'num') return numero(casas);
  if (coluna.tipo === 'pct') return numero(casas, '%');
  // Datas chegam em ISO (ordenam certo) e são mostradas como DD/MM/AAAA.
  if (coluna.tipo === 'data')
    return (p: ValueFormatterParams) => formatarData(typeof p.value === 'string' ? p.value : null);
  if (coluna.tipo === 'datahora')
    return (p: ValueFormatterParams) =>
      formatarDataHora(typeof p.value === 'string' ? p.value : null);
  return undefined;
}

/** CSS do legado (ex.: o mapa de cores do retorno mensal): "background-color: rgb(...); color: white". */
function cssDe(declaracoes: string): CellStyle {
  const estilo: CellStyle = {};
  for (const parte of declaracoes.split(';')) {
    const [prop, valor] = parte.split(':').map((x) => x?.trim());
    if (prop === 'background-color' && valor) estilo.backgroundColor = valor;
    else if (prop === 'color' && valor) estilo.color = valor;
  }
  return estilo;
}

export function estiloDe(classe: string | undefined): CellStyle | undefined {
  if (classe?.includes(':')) return cssDe(classe);
  const cor = classe ? CORES[classe] : undefined;
  if (!classe || !cor) return undefined;
  if (classe.startsWith('quartil') || classe === 'linha-destaque') return { backgroundColor: cor };
  return classe === 'destaque' ? { color: cor, fontWeight: 600 } : { color: cor };
}

/**
 * Chave de `tabela.estilos` de uma célula. Os estilos vêm indexados pela posição da linha
 * nos dados (`tabela.linhas`): usa `sourceRowIndex`, que não muda quando a tabela é
 * reordenada ou filtrada na tela (`rowIndex` é a posição exibida e deixava as cores nas
 * linhas erradas depois de ordenar por uma coluna).
 */
export function chaveEstilo(
  node: { sourceRowIndex: number } | null | undefined,
  campo: string,
): string {
  return `${node?.sourceRowIndex}:${campo}`;
}

/** Célula vazia que tem nota (o motivo de estar vazia) aparece como "—". */
function comNotas(
  formatar: ((p: ValueFormatterParams) => string) | undefined,
  campo: string,
  notas: Record<string, string> | undefined,
) {
  if (!notas) return formatar;
  return (p: ValueFormatterParams) => {
    if (p.value == null) return notas[chaveEstilo(p.node, campo)] ? '—' : '';
    return formatar ? formatar(p) : String(p.value);
  };
}

function LinkCelula({ value }: { value: unknown }) {
  if (typeof value !== 'string' || !value) return null;
  return (
    <a href={value} target="_blank" rel="noopener noreferrer">
      Abrir
    </a>
  );
}

export function TabelaDados({
  tabela,
  altura = 400,
  alturaAutomatica = false,
  fixarPrimeira = false,
  aoClicarLinha,
}: {
  tabela: TabelaApi;
  altura?: number;
  /** Ajusta a altura ao número de linhas (tabelas curtas, sem rolagem interna). */
  alturaAutomatica?: boolean;
  /** Mantém a primeira coluna visível quando a tabela rola na horizontal. */
  fixarPrimeira?: boolean;
  /** A linha inclui os campos da API e quaisquer extras (ex.: `id`) que não são colunas. */
  aoClicarLinha?: (linha: Record<string, unknown>) => void;
}) {
  const grade = useRef<AgGridReact>(null);
  const colunas = useMemo<ColDef[]>(
    () =>
      tabela.colunas.map((c, i) => ({
        field: c.campo,
        headerName: c.titulo,
        headerTooltip: c.descricao,
        pinned: fixarPrimeira && i === 0 ? 'left' : undefined,
        type: c.tipo === 'num' || c.tipo === 'pct' ? 'rightAligned' : undefined,
        valueFormatter: comNotas(formatador(c), c.campo, tabela.notas),
        tooltipValueGetter: tabela.notas
          ? (p) => tabela.notas?.[chaveEstilo(p.node, c.campo)]
          : undefined,
        ...(c.expandir ? { flex: 1, minWidth: 280 } : {}),
        // Link: abre em outra aba; a célula mostra só "Abrir".
        cellRenderer: c.tipo === 'link' ? LinkCelula : undefined,
        cellStyle: (p): CellStyle | null => {
          const chave = chaveEstilo(p.node, c.campo);
          const estilo = { ...estiloDe(tabela.estilos?.[chave]) };
          // `<linha>:<campo>:texto`: cor do texto por cima do fundo (ex.: quartil + sinal).
          const texto = estiloDe(tabela.estilos?.[`${chave}:texto`]);
          if (texto?.color) estilo.color = texto.color;
          return Object.keys(estilo).length ? estilo : null;
        },
      })),
    [fixarPrimeira, tabela],
  );
  // Ponto e vírgula: o Excel em pt-BR separa colunas assim. Os números saem
  // formatados (vírgula decimal), como na tela. O AG Grid já grava o BOM.
  const baixarCsv = () =>
    grade.current?.api.exportDataAsCsv({
      fileName: 'tabela.csv',
      columnSeparator: ';',
    });
  return (
    <Stack gap={6}>
      <Group justify="flex-end">
        <Button type="button" size="xs" variant="subtle" color="gray" onClick={baixarCsv}>
          Baixar CSV
        </Button>
      </Group>
      <div style={alturaAutomatica ? undefined : { height: altura }}>
        <AgGridReact
          ref={grade}
          theme={themeQuartz}
          rowData={tabela.linhas}
          columnDefs={colunas}
          domLayout={alturaAutomatica ? 'autoHeight' : 'normal'}
          // Campos vêm com os nomes de coluna do legado (ex.: "Retorno (a.a.)"): o ponto
          // não pode virar caminho aninhado.
          suppressFieldDotNotation
          // Seleção de texto como numa tabela HTML. ensureDomOrder alinha a ordem do
          // DOM à ordem exibida, senão o arraste copia as células erradas.
          enableCellTextSelection
          ensureDomOrder
          // Tooltips (título e notas) pelo `title` do navegador: sem camada extra na grade.
          enableBrowserTooltips
          onRowClicked={
            aoClicarLinha
              ? (evento) => {
                  if (evento.data) aoClicarLinha(evento.data);
                }
              : undefined
          }
          rowStyle={aoClicarLinha ? { cursor: 'pointer' } : undefined}
        />
      </div>
    </Stack>
  );
}
